/**
 * Stripe Connect for the operator (job 171a06d5).
 *
 * POST /operators/stripe/connect already existed with no surface, so a
 * stranger had no way from the console to set up payouts. This card calls
 * it and sends the browser to Stripe's hosted onboarding; Stripe sends the
 * operator back to the console.
 *
 * The status line reads only what the profile reports. Having an account
 * id is not the same as payouts being on: Stripe decides that after
 * onboarding, and the card says which of the two it is.
 *
 * Buyer-side funding is deliberately absent here: how a buyer pays is the
 * founder's structure call (top-up vs per-hire destination charge).
 */

import React, { useState } from 'react';
import { ApiError, type OperatorApi } from '../api/client';

interface Props {
  api: Pick<OperatorApi, 'connectStripe'>;
  stripeAccountId: string | null | undefined;
  payoutEnabled: boolean | undefined;
  /** Where Stripe returns the operator; defaults to this console page. */
  returnUrl?: string;
  /** Injected for tests; defaults to a full-page navigation. */
  redirect?: (url: string) => void;
}

const StripeConnectCard: React.FC<Props> = ({
  api,
  stripeAccountId,
  payoutEnabled,
  returnUrl,
  redirect = (url) => window.location.assign(url),
}) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const connected = Boolean(stripeAccountId);
  const status = !connected
    ? 'Not connected. Connect a Stripe account to be paid for work your stalls deliver.'
    : payoutEnabled
      ? 'Connected. Payouts are enabled.'
      : 'Connected, but Stripe has not enabled payouts yet. Finish onboarding with Stripe.';

  const start = async () => {
    setBusy(true);
    setError(null);
    try {
      const target = returnUrl ?? `${window.location.origin}/console`;
      const res = await api.connectStripe(target);
      redirect(res.onboarding_url);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'PAYMENT_NOT_CONFIGURED') {
        setError('Payments are not set up on this board yet, so Stripe cannot be connected here.');
      } else {
        setError(err instanceof Error ? err.message : 'Stripe connect failed.');
      }
      setBusy(false);
    }
  };

  return (
    <section className="console-card stripe-connect-card" aria-label="Stripe payouts">
      <h3 className="agent-section-header">Payouts (Stripe)</h3>
      <p className="onboarding-muted" data-testid="stripe-connect-status">{status}</p>
      {!(connected && payoutEnabled) && (
        <button type="button" className="action-btn primary" onClick={start} disabled={busy}>
          {busy ? 'Opening Stripe…' : connected ? 'Continue Stripe onboarding' : 'Connect Stripe'}
        </button>
      )}
      {error && <div className="auth-error" role="alert">{error}</div>}
    </section>
  );
};

export default StripeConnectCard;
